import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewChild } from '@angular/core';
import { DataSourceDirective } from 'eurogard-data-source/directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AlarmingSettingsPanelDataSource } from './alarming-settings-panel.data-source';
import {
  BoxedSlideToggleControlComponent,
} from 'angular-controls/boxed-slide-toggle-control/boxed-slide-toggle-control.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogService } from 'angular-components/confirm-dialog/confirm-dialog.service';
import { MatFormField } from '@angular/material/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  UserControllerSendRandomMailRemoteMethod,
} from 'open-api-legacy/remote-methods/user-controller-send-random-mail.remote-method';
import { DataSourceErrorComponent, DataSourceRefreshToMethodAdapterFactory } from 'eurogard-data-source';
import { NgIf } from '@angular/common';
import {
  UserControllerRandomSevenMessageRemoteMethod,
} from 'open-api-legacy/remote-methods/user-controller-random-seven-message.remote-method';
import {
  StatusControllerSmsStatusRemoteMethod,
} from 'open-api-legacy/remote-methods/status-controller-sms-status.remote-method';
import { MatIconModule } from '@angular/material/icon';
import {
  ChangeData,
  CountryISO,
  NgxIntlTelInputComponent,
  NgxIntlTelInputModule,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { I18nService } from 'angular-services/user-area/i18n.service';
import {
  NotificationAccordionSettingsControllerToggleAlarmingRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-toggle-alarming.remote-method';
import {
  NotificationAccordionSettingsControllerTogglePushNotificationRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-toggle-push-notification.remote-method';
import {
  NotificationAccordionSettingsControllerToggleEmailNotificationRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-toggle-email-notification.remote-method';
import {
  NotificationAccordionSettingsControllerSetTelegramNotificationRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-set-telegram-notification.remote-method';
import {
  NotificationAccordionSettingsControllerSetSMSNotificationRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-set-smsnotification.remote-method';
import {
  NotificationAccordionSettingsControllerSendTestNotificationRemoteMethod,
} from 'open-api-service-shared-features/remote-methods/notification-accordion-settings-controller-send-test-notification.remote-method';
import { FirebaseTokensDataSource } from 'angular-components/alarming-settings-panel/firebase-tokens.data-source';
import {
  UserControllerSendMessageToFirebaseRegistrationTokenRemoteMethod,
} from 'open-api-legacy/remote-methods/user-controller-send-message-to-firebase-registration-token.remote-method';
import {
  UserControllerDeleteFirebaseRegistrationTokenRemoteMethod,
} from 'open-api-legacy/remote-methods/user-controller-delete-firebase-registration-token.remote-method';
import { DateWithTimeAgoPipe } from 'eurogard-pipes';
import { ConfirmDirective } from '@rxap/components';
import { AlarmGroupFormComponent } from 'angular-forms/alarm-group-form/alarm-group-form.component';
import { UpdateUserActiveTimeMethod } from 'angular-components/alarming-settings-panel/update-user-active-time.method';
import { RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD } from 'eurogard-forms';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';

@Component({
  standalone: true,
  selector: 'eurogard-alarming-settings-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alarming-settings-panel.component.html',
  styleUrls: [ './alarming-settings-panel.component.scss' ],
  imports: [
    DataSourceDirective,
    MatProgressBarModule,
    BoxedSlideToggleControlComponent,
    FormsModule,
    MatDialogModule,
    MatFormField,
    MatInputModule,
    ReactiveFormsModule,
    MatButton,
    DataSourceErrorComponent,
    NgIf,
    MatIconModule,
    NgxIntlTelInputModule,
    MatIconButton,
    DateWithTimeAgoPipe,
    ConfirmDirective,
    AlarmGroupFormComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelContent,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
  ],
  providers: [
    ConfirmDialogService,
    AlarmingSettingsPanelDataSource,
    FirebaseTokensDataSource,
    UpdateUserActiveTimeMethod,
    {
      provide: RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD,
      useFactory: DataSourceRefreshToMethodAdapterFactory,
      deps: [AlarmingSettingsPanelDataSource],
    },
  ],
})
export class AlarmingSettingsPanelComponent {
  public readonly enforceCompactDesign = input<boolean>(false);

  public readonly panelDataSource = inject(AlarmingSettingsPanelDataSource);
  public readonly updating = signal(false);
  public readonly loading = computed(() => this.updating() || this.panelDataSource.loading());
  public readonly notificationAccordionData = toSignal(this.panelDataSource.connect({id: 'to-signal-settings-panel'}));

  protected readonly initialTimeslotsData = computed(() => {
    return {
      uuid: this.notificationAccordionData()!.uuid,
      timeslots: this.notificationAccordionData()!.timeslots
    };
  });

  preferredCountries = [CountryISO.Germany, CountryISO.Switzerland, CountryISO.France, CountryISO.Italy,CountryISO.UnitedKingdom];
  searchCountryField = [SearchCountryField.Iso2, SearchCountryField.Name];

  protected readonly useTelegramAlarming = signal(false);
  protected readonly telegramAPIToken = signal('');
  protected readonly telegramChatId = signal('');

  protected readonly smsServiceAvailable = signal(false);
  protected readonly useSMSAlarming = signal(false);
  protected readonly selectedCountryISO = signal(CountryISO.Germany);

  private readonly i18nService = inject(I18nService);
  protected readonly searchCountryPlaceholder = $localize`Search country`;

  phoneForm = new FormGroup({
    mobilePhone: new FormControl<ChangeData | undefined>(undefined, [Validators.required])
  });

  private readonly snackBar = inject(MatSnackBar);

  private readonly toggleAlarmingMethod = inject(NotificationAccordionSettingsControllerToggleAlarmingRemoteMethod);
  private readonly togglePushNotificationMethod  = inject(NotificationAccordionSettingsControllerTogglePushNotificationRemoteMethod);
  private readonly toggleEmailNotificationMethod = inject(NotificationAccordionSettingsControllerToggleEmailNotificationRemoteMethod);
  private readonly setTelegramNotification = inject(NotificationAccordionSettingsControllerSetTelegramNotificationRemoteMethod);
  private readonly setSMSNotification = inject(NotificationAccordionSettingsControllerSetSMSNotificationRemoteMethod);
  private readonly testPushNotificationRemoteMethod = inject(UserControllerSendMessageToFirebaseRegistrationTokenRemoteMethod);
  private readonly deleteFirebaseRegistrationTokenRemoteMethod = inject(UserControllerDeleteFirebaseRegistrationTokenRemoteMethod);
  private readonly testEmailNotificationRemoteMethod = inject(UserControllerSendRandomMailRemoteMethod);
  private readonly testTelegramNotificationRemoteMethod = inject(NotificationAccordionSettingsControllerSendTestNotificationRemoteMethod);
  private readonly testSMSNotificationRemoteMethod = inject(UserControllerRandomSevenMessageRemoteMethod);

  protected readonly updateUserActiveTimeMethod = inject(UpdateUserActiveTimeMethod);

  private readonly firebaseTokensDataSource = inject(FirebaseTokensDataSource);
  protected firebaseTokensData = toSignal(this.firebaseTokensDataSource.connect({ id: 'firebase-tokens' }));

  private readonly statusControllerSmsStatusRemoteMethod = inject(StatusControllerSmsStatusRemoteMethod);

  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected get hasCurrentTelegramCredentials() {
    return this.notificationAccordionData()!.telegramChatId != null && this.notificationAccordionData()!.telegramChatId != null;
  }

  protected get hasCurrentMobilePhone() {
    return this.notificationAccordionData()!.mobilePhone != null;
  }

  @ViewChild('phoneInput') phoneInput?: NgxIntlTelInputComponent;

  constructor() {
    effect(() => {
      const data = this.notificationAccordionData();
      if (data) {
        this.useTelegramAlarming.set(data.telegramNotificationEnabled);
        this.useSMSAlarming.set(data.smsNotificationEnabled);
        this.telegramChatId.set(data.telegramChatId);
        this.telegramAPIToken.set('');
        this.phoneInput?.setDisabledState(!data.smsNotificationEnabled);
      }
    }, { allowSignalWrites: true });

    this.statusControllerSmsStatusRemoteMethod.call().then((response) => {
      if ('sms' in response && response['sms']) {
        this.smsServiceAvailable.set(true);
      }
    });

    switch (this.i18nService.currentLanguage()) {
      case 'fr':
        this.selectedCountryISO.set(CountryISO.Switzerland);
        break;
      case 'it':
        this.selectedCountryISO.set(CountryISO.Switzerland);
        break;
      case 'en':
        this.selectedCountryISO.set(CountryISO.UnitedKingdom);
        break;
      default:
        this.selectedCountryISO.set(CountryISO.Germany);
        break;
    }
  }

  protected get saveButtonLabelTelegram() {
    if (!this.hasCurrentTelegramCredentials) {
      return $localize`Save new credentials`;
    } else {
      if (this.telegramAPIToken() === '') {
        return $localize`Update Chat ID`;
      } else {
        return $localize`Override current credentials`;
      }
    }
  }

  public async toggleAlarming(newValue: boolean, alarmingToggle: BoxedSlideToggleControlComponent) {
    this.updating.set(true);
    const confirmed = await this.confirmDialogService.confirm(
      $localize`Are you sure you want to ${ newValue ? 'enable' : 'disable' } alarming?`
    );
    if (confirmed) {
      await this.toggleAlarmingMethod.call({
        parameters: {
          uuid: this.notificationAccordionData()!.uuid,
        },
      });
      this.panelDataSource.refresh();
    } else {
      alarmingToggle.writeValue(!newValue);
    }
    this.updating.set(false);
  }

  public async toggleEmailNotification(newValue: boolean, emailNotificationsToggle: BoxedSlideToggleControlComponent) {
    this.updating.set(true);
    const confirmed = await this.confirmDialogService.confirm($localize`Are you sure you want to ${ newValue ?
      'enable' :
      'disable' } email notification?`);
    if (confirmed) {
      await this.toggleEmailNotificationMethod.call({
        parameters: {
          uuid: this.notificationAccordionData()!.uuid,
        },
      });
      this.panelDataSource.refresh();
    } else {
      emailNotificationsToggle.writeValue(!newValue);
    }
    this.updating.set(false);
  }

  public async togglePushNotification(newValue: boolean, pushNotificationsToggle: BoxedSlideToggleControlComponent) {
    this.updating.set(true);
    const confirmed = await this.confirmDialogService.confirm($localize`Are you sure you want to ${ newValue ?
      'enable' :
      'disable' } push notification?`);
    if (confirmed) {
      await this.togglePushNotificationMethod.call({
        parameters: {
          uuid: this.notificationAccordionData()!.uuid,
        },
      });
      this.panelDataSource.refresh();
    } else {
      pushNotificationsToggle.writeValue(!newValue);
    }
    this.updating.set(false);
  }

  protected async toggleTelegramNotification(newValue: boolean, telegramNotificationsToggle: BoxedSlideToggleControlComponent) {
    if (
      newValue && !this.notificationAccordionData()!.telegramNotificationEnabled && this.hasCurrentTelegramCredentials ||
      !newValue && this.notificationAccordionData()!.telegramNotificationEnabled
    ) {
      this.updating.set(true);
      const confirmed = await this.confirmDialogService.confirm($localize`Are you sure you want to ${ newValue ?
        'enable' :
        'disable' } telegram notifications?`);
      if (confirmed) {
        await this.setTelegramNotification.call({
          parameters: {
            uuid: this.notificationAccordionData()!.uuid,
          },
          requestBody: {
            telegramNotificationEnabled: newValue,
          },
        });
        this.panelDataSource.refresh();
      } else {
        telegramNotificationsToggle.writeValue(!newValue);
      }
      this.updating.set(false);
    } else {
      this.useTelegramAlarming.set(newValue);
      telegramNotificationsToggle.writeValue(newValue);
    }
  }

  protected async toggleSMSNotification(newValue: boolean, smsNotificationsToggle: BoxedSlideToggleControlComponent) {
    if (!this.smsServiceAvailable()) {
      return;
    }
    if (
      newValue && !this.notificationAccordionData()!.smsNotificationEnabled && this.hasCurrentMobilePhone ||
      !newValue && this.notificationAccordionData()!.smsNotificationEnabled
    ) {
      this.updating.set(true);
      const confirmed = await this.confirmDialogService.confirm($localize`Are you sure you want to ${ newValue ?
        'enable' :
        'disable' } sms notifications? This is a paid service!`);
      if (confirmed) {
        await this.setSMSNotification.call({
          parameters: {
            uuid: this.notificationAccordionData()!.uuid,
          },
          requestBody: {
            smsNotificationEnabled: newValue,
          },
        });
        this.panelDataSource.refresh();
      } else {
        smsNotificationsToggle.writeValue(!newValue);
        this.phoneInput?.setDisabledState(newValue);
      }
      this.updating.set(false);
    } else {
      this.useSMSAlarming.set(newValue);
      smsNotificationsToggle.writeValue(newValue);
      this.phoneInput?.setDisabledState(!newValue);
    }
  }

  protected async saveTelegramCredentials() {
    this.updating.set(true);
    const telegramNotificationEnabled = this.useTelegramAlarming();
    const telegramApiToken = telegramNotificationEnabled
      ? this.telegramAPIToken() !== '' ? this.telegramAPIToken() : undefined
      : undefined;
    const telegramChatId = this.telegramChatId() ? this.telegramChatId() : undefined;
    await this.setTelegramNotification.call({
      parameters: {
        uuid: this.notificationAccordionData()!.uuid,
      },
      requestBody: {
        telegramNotificationEnabled,
        telegramApiToken,
        telegramChatId,
      },
    });
    this.panelDataSource.refresh();
    this.updating.set(false);
  }

  protected async saveMobilePhone() {
    this.updating.set(true);
    const smsNotificationEnabled = this.useSMSAlarming();
    const mobilePhoneValue = this.phoneForm.get('mobilePhone')?.value?.e164Number;
    const mobilePhone = smsNotificationEnabled
      ? mobilePhoneValue !== '' && mobilePhoneValue !== null ? mobilePhoneValue : undefined
      : undefined;
    await this.setSMSNotification.call({
      parameters: {
        uuid: this.notificationAccordionData()!.uuid,
      },
      requestBody: {
        smsNotificationEnabled,
        mobilePhone,
      },
    });
    this.phoneForm.get('mobilePhone')?.reset();
    this.panelDataSource.refresh();
    this.updating.set(false);
  }

  protected async sendPushTestNotification(fcmUuid: string) {
    this.updating.set(true);
    this.testPushNotificationRemoteMethod
    .call({ parameters: { uuid: this.notificationAccordionData()!.uuid, 'fcm-uuid': fcmUuid } })
    .then(() => {
      this.snackBar.open($localize`Test notification sent`, 'OK', {
        duration: 5000,
      });
    })
    .catch(err => {
      console.error(err);
      this.snackBar.open($localize`Failed to send test notification`, 'OK', {
        duration: 5000,
      });
    })
    .finally(() => this.updating.set(false));
  }

  protected deleteFirebaseToken(fcmUuid: string) {
    this.updating.set(true);
    this.deleteFirebaseRegistrationTokenRemoteMethod
    .call({ parameters: { 'user-uuid': this.notificationAccordionData()!.uuid, 'fcm-uuid': fcmUuid } })
    .then(() => {
      this.snackBar.open($localize`Firebase token deleted`, 'OK', {
        duration: 5000,
      });
    })
    .finally(() => {
      this.firebaseTokensDataSource.refresh();
      this.updating.set(false);
    });
  }

  protected async sendEmailTestNotification() {
    this.updating.set(true);
    this.testEmailNotificationRemoteMethod
    .call({ parameters: { uuid: this.notificationAccordionData()!.uuid } })
    .then(() => {
      this.snackBar.open($localize`Test notification sent`, 'OK', {
        duration: 5000,
      });
    })
    .catch(err => {
      console.error(err);
      this.snackBar.open($localize`Failed to send test notification`, 'OK', {
        duration: 5000,
      });
    })
    .finally(() => this.updating.set(false));
  }

  protected async sendTelegramTestNotification() {
    this.updating.set(true);
    this.testTelegramNotificationRemoteMethod
    .call({ parameters: { uuid: this.notificationAccordionData()!.uuid } })
    .then(() => {
      this.snackBar.open($localize`Test notification sent`, 'OK', {
        duration: 5000,
      });
    })
    .catch(err => {
      console.error(err);
      this.snackBar.open($localize`Failed to send test notification`, 'OK', {
        duration: 5000,
      });
    })
    .finally(() => this.updating.set(false));
  }

  protected async sendSMSTestNotification() {
    this.updating.set(true);
    this.testSMSNotificationRemoteMethod
    .call({ parameters: { uuid: this.notificationAccordionData()!.uuid } })
    .then(() => {
      this.snackBar.open($localize`Test notification sent`, 'OK', {
        duration: 5000,
      });
    })
    .catch(err => {
      console.error(err);
      this.snackBar.open($localize`Failed to send test notification`, 'OK', {
        duration: 5000,
      });
    })
    .finally(() => this.updating.set(false));
  }
}
